/**
 * AgendaSetupWizard — Wizard 5 pasos: primera configuración de Agenda de Citas
 * Sprint 07-D: UX modal con barra de progreso, selección por perfil y activación Kanban
 */
import React, { useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import LinearProgress from "@material-ui/core/LinearProgress";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Switch from "@material-ui/core/Switch";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import Paper from "@material-ui/core/Paper";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import SpaIcon from "@material-ui/icons/Spa";
import WorkIcon from "@material-ui/icons/Work";
import FavoriteIcon from "@material-ui/icons/Favorite";
import SchoolIcon from "@material-ui/icons/School";
import BuildIcon from "@material-ui/icons/Build";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";
import { completeOnboarding, activateOnboarding } from "../../services/onboarding.api";
import toastError from "../../errors/toastError";
import { useStyles } from "./styles";
import { withAlpha } from "../../theme/agendaTheme";
import { BUSINESS_PROFILES, TIMEZONES, CURRENCIES } from "./appointments.config.constants";

// ─── Data estática ──────────────────────────────────────────────────────────

// Perfiles, husos y monedas viven en appointments.config.constants (compartidos con
// BusinessDataDialog). Aquí solo se les añade el icono, que es propio del wizard.
const PROFILE_ICONS = { 1: SpaIcon, 2: WorkIcon, 3: FavoriteIcon, 4: SchoolIcon, 5: BuildIcon };
const PROFILES = BUSINESS_PROFILES.map(p => ({ ...p, Icon: PROFILE_ICONS[p.id] }));

const KANBAN_PREVIEW = {
  1: [
    { label: "Nuevo Lead",         bg: "#3B82F6" },
    { label: "Cita Agendada",      bg: "#F97316" },
    { label: "En Servicio",        bg: "#0F766E" },
    { label: "Servicio Completado",bg: "#16A34A" },
    { label: "Cancelado",          bg: "#EF4444" },
  ],
  2: [
    { label: "Nuevo Lead",         bg: "#3B82F6" },
    { label: "Reunión Agendada",   bg: "#F97316" },
    { label: "Propuesta Enviada",  bg: "#8B5CF6" },
    { label: "En Negociación",     bg: "#0F766E" },
    { label: "Cerrado Ganado",     bg: "#16A34A" },
    { label: "Perdido",            bg: "#EF4444" },
  ],
  3: [
    { label: "Nuevo Paciente",     bg: "#3B82F6" },
    { label: "Consulta Inicial",   bg: "#F97316" },
    { label: "En Tratamiento",     bg: "#0F766E" },
    { label: "Control Programado", bg: "#06B6D4" },
    { label: "Alta Médica",        bg: "#16A34A" },
    { label: "Abandonó",           bg: "#EF4444" },
  ],
  4: [
    { label: "Interesado",         bg: "#3B82F6" },
    { label: "Sesión de Prueba",   bg: "#F97316" },
    { label: "Sesión Confirmada",  bg: "#0F766E" },
    { label: "En Curso",           bg: "#06B6D4" },
    { label: "Sesión Completada",  bg: "#16A34A" },
    { label: "Abandonó/Pausó",     bg: "#EF4444" },
  ],
  5: [
    { label: "Solicitud",          bg: "#3B82F6" },
    { label: "Diagnóstico",        bg: "#F97316" },
    { label: "Presupuesto",        bg: "#8B5CF6" },
    { label: "En Reparación",      bg: "#0F766E" },
    { label: "Entregado",          bg: "#16A34A" },
    { label: "Garantía",           bg: "#EF4444" },
  ],
};

const SLOT_OPTS  = [15, 30, 45, 60, 90, 120];
const DAY_NAMES  = { 1:"Lun", 2:"Mar", 3:"Mié", 4:"Jue", 5:"Vie", 6:"Sáb", 7:"Dom" };

const DEFAULT_HOURS = [1,2,3,4,5,6,7].map(d => ({
  dayOfWeek: d,
  isOpen:    d <= 6,
  openTime:  d === 6 ? "09:00" : "09:00",
  closeTime: d === 6 ? "13:00" : "18:00",
}));

const EMPTY_PROF = { name: "", roleLabel: "", specialty: "" };
const EMPTY_SVC  = { serviceName: "", durationMin: 60, price: "" };

const STEP_TITLES = [
  "¿Cuál es tu tipo de negocio?",
  "Datos del negocio",
  "Horarios de atención",
  "Equipo y Catálogo de servicios",
  "Vista previa y Activación",
];

// ─── Componente principal ───────────────────────────────────────────────────

// eslint-disable-next-line react/prop-types
export default function AgendaSetupWizard({ open, onClose, onCompleted }) {
  const classes = useStyles();
  const [step,          setStep]          = useState(1);
  const [profileId,     setProfileId]     = useState(null);
  const [profileName,   setProfileName]   = useState("");
  const [businessName,  setBusinessName]  = useState("");
  const [timezone,      setTimezone]      = useState("America/Lima");
  const [currency,      setCurrency]      = useState("PEN");
  const [hours,         setHours]         = useState(DEFAULT_HOURS);
  const [slot,          setSlot]          = useState(60);
  const [professionals, setProfessionals] = useState([{ ...EMPTY_PROF }]);
  const [services,      setServices]      = useState([{ ...EMPTY_SVC }]);
  const [saving,        setSaving]        = useState(false);

  const canNext = () => {
    if (step === 1) return profileId !== null;
    if (step === 2) return businessName.trim().length > 0;
    return true;
  };

  const handleSelectProfile = (p) => { setProfileId(p.id); setProfileName(p.name); };
  const handleNext = () => setStep(s => s + 1);
  const handleBack = () => setStep(s => s - 1);
  const handleClose = () => { if (!saving) onClose && onClose(); };

  // Horarios
  const toggleDay   = (i, v)      => { const n = [...hours]; n[i] = { ...n[i], isOpen: v }; setHours(n); };
  const changeTime  = (i, f, v)   => { const n = [...hours]; n[i] = { ...n[i], [f]: v };    setHours(n); };

  // Profesionales
  const addProf    = ()       => setProfessionals(p => [...p, { ...EMPTY_PROF }]);
  const removeProf = (i)      => setProfessionals(p => p.filter((_, x) => x !== i));
  const changeProf = (i,f,v)  => setProfessionals(p => p.map((it, x) => x === i ? { ...it, [f]: v } : it));

  // Servicios
  const addSvc    = ()       => setServices(s => [...s, { ...EMPTY_SVC }]);
  const removeSvc = (i)      => setServices(s => s.filter((_, x) => x !== i));
  const changeSvc = (i,f,v)  => setServices(s => s.map((it, x) => x === i ? { ...it, [f]: v } : it));

  // Activar
  const handleActivate = async () => {
    setSaving(true);
    try {
      await completeOnboarding({
        profileId,
        profileName,
        businessName: businessName.trim(),
        timezone,
        currency,
        businessHours: hours.map(h => ({ ...h, slotDuration: slot })),
        professionals: professionals.filter(p => p.name.trim()),
        services: services
          .filter(s => s.serviceName.trim())
          .map(s => ({
            serviceName: s.serviceName.trim(),
            durationMin: Number(s.durationMin) || 60,
            price:       s.price !== "" ? Number(s.price) : 0,
          })),
      });
      await activateOnboarding({ profileId });
      toast.success("¡Agenda de Citas activada correctamente!");
      onCompleted && onCompleted();
    } catch (e) {
      toastError(e);
    } finally {
      setSaving(false);
    }
  };

  // ── Render por paso ────────────────────────────────────────────────────────

  const renderStep1 = () => (
    <Box>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
        Esto adapta el módulo de Agenda, el Kanban y la tarjeta del contacto a tu sector.
      </Typography>
      {PROFILES.map(p => {
        const selected = profileId === p.id;
        const { Icon } = p;
        return (
          <Paper key={p.id} onClick={() => handleSelectProfile(p)} elevation={0}
            className={classes.wizardCard}
            style={selected ? { borderColor: p.color, backgroundColor: withAlpha(p.color, 0.1) } : undefined}
          >
            <div style={{
              width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
              backgroundColor: withAlpha(p.color, 0.16),
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
            >
              <Icon style={{ fontSize: 24, color: p.color }} />
            </div>
            <div style={{ flex: 1 }}>
              <Typography variant="subtitle2" style={{ fontWeight: 700, color: selected ? p.color : "inherit" }}>
                {p.name}
              </Typography>
              <Typography variant="caption" color="textSecondary">{p.desc}</Typography>
            </div>
            {selected && <CheckCircleIcon style={{ color: p.color, fontSize: 26, flexShrink: 0 }} />}
          </Paper>
        );
      })}
    </Box>
  );

  const renderStep2 = () => (
    <Box>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
        Esta información será usada por el Agente IA en sus mensajes automáticos a tus clientes.
      </Typography>
      <TextField
        label="Nombre del negocio" fullWidth variant="outlined" required
        value={businessName} onChange={e => setBusinessName(e.target.value)}
        placeholder="Ej: Clínica Bienestar, Colegio San Marcos…"
        style={{ marginBottom: 16 }}
      />
      <FormControl fullWidth variant="outlined" style={{ marginBottom: 16 }}>
        <InputLabel>Zona horaria</InputLabel>
        <Select value={timezone} onChange={e => setTimezone(e.target.value)} label="Zona horaria">
          {TIMEZONES.map(tz => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl fullWidth variant="outlined">
        <InputLabel>Moneda</InputLabel>
        <Select value={currency} onChange={e => setCurrency(e.target.value)} label="Moneda">
          {CURRENCIES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
        </Select>
      </FormControl>
    </Box>
  );

  const renderStep3 = () => (
    <Box>
      <Box className={classes.wizardPanelDashed}>
        <Typography variant="body2" style={{ fontWeight: 600, flexShrink: 0 }}>Duración del turno:</Typography>
        <FormControl size="small" variant="outlined" style={{ minWidth: 110 }}>
          <Select value={slot} onChange={e => setSlot(Number(e.target.value))}>
            {SLOT_OPTS.map(m => <MenuItem key={m} value={m}>{m} min</MenuItem>)}
          </Select>
        </FormControl>
        <Typography variant="caption" color="textSecondary">Intervalo entre citas disponibles.</Typography>
      </Box>
      {hours.map((day, idx) => (
        <Box key={day.dayOfWeek} className={classes.wizardRow}>
          <Switch checked={!!day.isOpen} onChange={e => toggleDay(idx, e.target.checked)} color="primary" size="small" />
          <Typography variant="subtitle2"
            className={day.isOpen ? undefined : classes.configDayClosed}
            style={{ width: 38, fontWeight: 700 }}
          >
            {DAY_NAMES[day.dayOfWeek]}
          </Typography>
          {day.isOpen ? (
            <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <TextField type="time" size="small" variant="outlined" value={day.openTime}
                onChange={e => changeTime(idx, "openTime", e.target.value)}
                className={classes.nativeDateInput}
                style={{ width: 120 }} inputProps={{ style: { fontSize: "0.85rem", padding: "6px 8px" } }}
              />
              <Typography variant="caption" color="textSecondary">—</Typography>
              <TextField type="time" size="small" variant="outlined" value={day.closeTime}
                onChange={e => changeTime(idx, "closeTime", e.target.value)}
                className={classes.nativeDateInput}
                style={{ width: 120 }} inputProps={{ style: { fontSize: "0.85rem", padding: "6px 8px" } }}
              />
            </Box>
          ) : (
            <Typography variant="caption" className={classes.configClosedLabel}>
              CERRADO
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );

  const renderStep4 = () => (
    <Box>
      <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 4 }}>Tu equipo de trabajo</Typography>
      <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 12 }}>
        Los clientes podrán elegir con quién prefieren ser atendidos.
      </Typography>
      {professionals.map((p, i) => (
        <Box key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
          <TextField size="small" variant="outlined" label="Nombre" style={{ flex: 2 }}
            value={p.name} onChange={e => changeProf(i, "name", e.target.value)} placeholder="Ej: Carlos López"
          />
          <TextField size="small" variant="outlined" label="Rol" style={{ flex: 1.5 }}
            value={p.roleLabel} onChange={e => changeProf(i, "roleLabel", e.target.value)} placeholder="Ej: Docente"
          />
          <TextField size="small" variant="outlined" label="Especialidad" style={{ flex: 2 }}
            value={p.specialty} onChange={e => changeProf(i, "specialty", e.target.value)} placeholder="Ej: Matemáticas"
          />
          <IconButton size="small" onClick={() => removeProf(i)} disabled={professionals.length === 1}>
            <DeleteIcon className={classes.configDeleteIcon} style={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={addProf} className={classes.wizardLinkButton} style={{ marginBottom: 24 }}>
        + Agregar profesional
      </Button>

      <div className={classes.configDivider} />

      <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 4 }}>Catálogo de servicios</Typography>
      <Typography variant="caption" color="textSecondary" display="block" style={{ marginBottom: 12 }}>
        El Agente IA los presentará al contacto al inicio de cada conversación.
      </Typography>
      {services.map((s, i) => (
        <Box key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 8 }}>
          <TextField size="small" variant="outlined" label="Servicio" style={{ flex: 3 }}
            value={s.serviceName} onChange={e => changeSvc(i, "serviceName", e.target.value)} placeholder="Ej: Consulta general"
          />
          <TextField size="small" variant="outlined" label="Duración (min)" type="number" style={{ flex: 1.5 }}
            value={s.durationMin} onChange={e => changeSvc(i, "durationMin", e.target.value)} inputProps={{ min: 1 }}
          />
          <TextField size="small" variant="outlined" label="Precio" type="number" style={{ flex: 1.5 }}
            value={s.price} onChange={e => changeSvc(i, "price", e.target.value)} inputProps={{ min: 0, step: "0.01" }}
          />
          <IconButton size="small" onClick={() => removeSvc(i)} disabled={services.length === 1}>
            <DeleteIcon className={classes.configDeleteIcon} style={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      ))}
      <Button size="small" startIcon={<AddIcon />} onClick={addSvc} className={classes.wizardLinkButton}>
        + Agregar servicio
      </Button>
    </Box>
  );

  const renderStep5 = () => {
    const cols = KANBAN_PREVIEW[profileId] || [];
    return (
      <Box>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
          Se crearán estas columnas en el tablero Kanban según el perfil <strong>{profileName}</strong>.
        </Typography>

        <Paper elevation={0} className={classes.wizardPanel} style={{ marginBottom: 20 }}>
          <Typography variant="caption" className={classes.configEyebrow} style={{ display: "block", marginBottom: 12 }}>
            Vista previa columnas Kanban
          </Typography>
          <Box style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {cols.map(col => (
              <Chip key={col.label} label={col.label}
                style={{ backgroundColor: col.bg, color: "#fff", fontWeight: 600, fontSize: "0.78rem" }}
              />
            ))}
          </Box>
        </Paper>

        <Paper elevation={0} className={classes.wizardPanel}>
          <Typography variant="caption" className={classes.configEyebrow} style={{ display: "block", marginBottom: 10 }}>
            Resumen de configuración
          </Typography>
          <Box style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <Typography variant="body2"><strong>Negocio:</strong> {businessName}</Typography>
            <Typography variant="body2"><strong>Perfil:</strong> {profileName}</Typography>
            <Typography variant="body2"><strong>Zona horaria:</strong> {timezone} &nbsp;·&nbsp; <strong>Moneda:</strong> {currency}</Typography>
            <Typography variant="body2">
              <strong>Días activos:</strong> {hours.filter(h => h.isOpen).length} &nbsp;·&nbsp;
              <strong>Turno:</strong> {slot} min
            </Typography>
            <Typography variant="body2">
              <strong>Profesionales:</strong> {professionals.filter(p => p.name.trim()).length} &nbsp;·&nbsp;
              <strong>Servicios:</strong> {services.filter(s => s.serviceName.trim()).length}
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  };

  const STEP_RENDERS = [renderStep1, renderStep2, renderStep3, renderStep4, renderStep5];
  const progress = ((step - 1) / 4) * 100;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
      disableBackdropClick={saving} disableEscapeKeyDown={saving}
    >
      {/* Cabecera */}
      <Box style={{ padding: "22px 28px 0" }}>
        <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <Typography variant="caption" className={classes.configEyebrow}>
            CONFIGURACIÓN INICIAL · AGENDA DE CITAS
          </Typography>
          <Chip label={`Paso ${step} / 5`} size="small"
            className={step === 5 ? classes.wizardStepChipFinal : classes.wizardStepChip}
          />
        </Box>
        <Typography variant="h5" style={{ fontWeight: 800, marginBottom: 14, lineHeight: 1.3 }}>
          {STEP_TITLES[step - 1]}
        </Typography>
        <LinearProgress variant="determinate" value={progress} style={{ height: 4, borderRadius: 2 }} color="primary" />
      </Box>

      {/* Contenido */}
      <DialogContent style={{ paddingTop: 24, minHeight: 360, maxHeight: "58vh", overflowY: "auto" }}>
        {STEP_RENDERS[step - 1]()}
      </DialogContent>

      {/* Pie */}
      <DialogActions className={classes.wizardActions}>
        <Button onClick={handleBack} disabled={step === 1 || saving}
          style={{ visibility: step === 1 ? "hidden" : "visible" }}
        >
          ← Anterior
        </Button>
        <div style={{ flex: 1 }} />
        {step < 5 ? (
          <Button variant="contained" color="primary" onClick={handleNext}
            disabled={!canNext()} style={{ minWidth: 130 }}
          >
            Siguiente →
          </Button>
        ) : (
          <Button variant="contained" onClick={handleActivate} disabled={saving}
            className={classes.wizardActivateButton}
          >
            {saving ? "Activando…" : "✓  ACTIVAR AGENDA"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
